import React, { createContext, useContext, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  LocalizationProvider,
  UploadConfigurationProvider,
  UploadManagerProvider,
  UploadManagerProviderProps,
  basicImageFileTypeMappings,
} from '@design-stack-vista/upload-components'
import { useIdentity } from '@vp/ubik-context'
import { SubBrands, Tenants, VistaAsset } from '@design-stack-vista/vista-assets-sdk'
import { IMAGE_UPLOAD_STATES, MAX_PHOTO_UPLOADS } from 'client/constants'
import { RenderProperty } from 'shared/renderProperties'
import { booleanRenderPropertySelector } from 'client/store/config'
import { getLogger } from 'shared/logger'
import { usePersonalizationManager } from './PersonalizationManager'
import { SelectedAsset } from '../Schemes/store/types'

interface UploadsStoreContextProps {
  selectedUploads: SelectedAsset[]
  setSelectedUploads: React.Dispatch<React.SetStateAction<SelectedAsset[]>>
  imageUploadState: { status: string; message?: string }
  setImageUploadState: React.Dispatch<React.SetStateAction<{ status: string; message?: string }>>
}

const UploadsStoreContext = createContext<UploadsStoreContextProps | undefined>(undefined)

export const UploadsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedUploads, setSelectedUploads] = useState<SelectedAsset[]>([])
  const [imageUploadState, setImageUploadState] = useState<{ status: string; message?: string }>({
    status: IMAGE_UPLOAD_STATES.READY,
  })
  const { identity } = useIdentity()
  const LOGGER = getLogger()

  const { translations, tracking } = usePersonalizationManager()
  const {
    deletingLabel,
    deleteConfirmationHeader,
    deleteConfirmationDescription,
    deleteConfirmationCancel,
    deleteConfirmationConfirm,
    deleteButtonTitle,
    assetInUseLabel,
    loadingLabel,
    uploadingLabel,
  } = translations.uploadsCopy

  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const smartCropEnabled = booleanRenderProperty(RenderProperty.PersonalizationSmartCropEnabled)

  const uploadAuthProvider = useMemo(
    () =>
      identity
        ? {
            getAuthHeaders: async (): Promise<{ Authorization: string }> => ({ Authorization: `Bearer ${identity?.accessToken}` }),
          }
        : undefined,
    [identity],
  )

  const handleAssetDelete = async (asset: VistaAsset): Promise<void> => {
    // Remove deleted asset from selected photos to prevent selection state from desyncing
    setSelectedUploads((prevUploads) => prevUploads.filter((upload) => upload.asset.data?.id !== asset.data?.id))
    tracking.imageDeleted()
  }

  const handleUploadError = (error?: Error, uploadedAssets?: VistaAsset[]): void => {
    setImageUploadState({ status: IMAGE_UPLOAD_STATES.ERROR, message: error?.message })
    LOGGER.error(`Failed to upload image(s): ${error}`, error, { uploadedAssets })
    tracking.imageUploadError(error)
  }

  const handleUploadComplete = (asset: VistaAsset): void => {
    setSelectedUploads((prevSelectedUploads) => [...prevSelectedUploads, { asset, pageNumber: 1 }])
    setImageUploadState({ status: IMAGE_UPLOAD_STATES.READY })

    if (smartCropEnabled) {
      const saliencyMapUrl = asset.getStackedVariantUrl({
        variants: [{ variantName: 'saliencyMap' }],
        includeSignature: true,
      })
      fetch(saliencyMapUrl)
    }

    tracking.imageUploadCompleted()
  }

  const handleUploadStart: UploadManagerProviderProps['onUpload'] = async (selectedAssets, _uploadedBlobs, nonUploadedBlobs) => {
    setImageUploadState({ status: IMAGE_UPLOAD_STATES.LOADING })

    if (!selectedAssets.length || nonUploadedBlobs.length) {
      setImageUploadState({ status: IMAGE_UPLOAD_STATES.READY })
      return
    }

    tracking.imageUploadStarted()

    const uploadedAssets = (await Promise.all(selectedAssets)).filter((asset) => !!asset) as VistaAsset[]
    const allAssetsUploaded = uploadedAssets.length === selectedAssets.length

    if (!allAssetsUploaded) {
      setImageUploadState({ status: IMAGE_UPLOAD_STATES.ERROR })
    }
  }

  const value = useMemo(
    () => ({
      selectedUploads,
      setSelectedUploads,
      imageUploadState,
      setImageUploadState,
    }),
    [selectedUploads, imageUploadState],
  )

  return (
    <UploadsStoreContext.Provider value={value}>
      <UploadConfigurationProvider
        maxUploads={MAX_PHOTO_UPLOADS}
        supportedFileTypes={basicImageFileTypeMappings.filter((type) => type.fileType !== 'application/pdf')}
      >
        <UploadManagerProvider
          assetsExpireOn={process.env.NODE_ENV === 'production' ? 'never' : 'P1D'}
          authProvider={uploadAuthProvider}
          experience="gallery-brand-personalization"
          subBrand={SubBrands.VistaPrint}
          writeTenant={process.env.NODE_ENV === 'production' ? Tenants.VistaPrint : Tenants.VistaNonProd}
          onAssetDeleted={handleAssetDelete}
          onError={handleUploadError}
          onUpload={handleUploadStart}
          onUploadCompleted={handleUploadComplete}
        >
          <LocalizationProvider
            localizedValues={{
              deletingLabel,
              deleteConfirmationKeys: {
                closeButton: 'Close Delete Image Dialog',
                dialogTitle: deleteConfirmationHeader,
                dialogMessage: deleteConfirmationDescription,
                cancelButton: deleteConfirmationCancel,
                confirmButton: deleteConfirmationConfirm,
              },
              deleteButtonTitle,
              assetInUseLabel,
              loadingLabel,
              uploadingLabel,
            }}
          >
            {children}
          </LocalizationProvider>
        </UploadManagerProvider>
      </UploadConfigurationProvider>
    </UploadsStoreContext.Provider>
  )
}

export const useUploadsStore = (): UploadsStoreContextProps => {
  const context = useContext(UploadsStoreContext)

  if (!context) {
    throw new Error('useUploadsStore must be used within an UploadsStoreProvider')
  }
  return context
}
